import { faCheck, faEllipsisVertical, faTrash, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, DropdownMenu, IconButton } from '@nexelem/components';
import { type SelectedSavedViewData, useSaveViewStore } from '../SaveViewStore';
import { useTableName } from '../../utils';
import { useTableTranslation } from '../../translations';
import { useFormContext } from 'react-hook-form';
import { Decoder } from '../../filters/Decoder';
import { cn } from '@nexelem/utils';
import { OverrideCurrentViewButton } from './OverrideCurrentViewButton';

type SavedViewItemProps = {
  data: SelectedSavedViewData;
};

export const SavedViewItem = ({ data }: SavedViewItemProps) => {
  const { name, id, description } = data;

  const { t } = useTableTranslation();
  const tableName = useTableName();
  const { reset } = useFormContext();
  const isDefault = useSaveViewStore((state) => state.defaultViews[tableName] === id);
  const setDefaultView = useSaveViewStore((state) => state.setDefaultView);
  const deleteView = useSaveViewStore((state) => state.deleteView);

  const handleSetDefault = async () => {
    const object = await Decoder.decode(data.data);

    setDefaultView(tableName, id);
    reset(object);
  };

  const handleRemoveDefault = () => {
    setDefaultView(tableName, '');
    reset();
  };

  const handleDelete = () => {
    deleteView(tableName, id);
  };

  return (
    <div
      className={cn(
        'box-border flex items-center justify-between rounded-xs border border-border-secondary bg-surface-container-lighter px-4 py-2 text-s16',
        isDefault && 'border-border-focus-lighter bg-surface-focus-lighter',
      )}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-s16-sb">{name}</span>
          {isDefault && <Badge variant="highlighted">{t('default')}</Badge>}
        </div>
        <div className="text-t12-sb text-text-secondary-darker">{description}</div>
      </div>

      <div className="flex items-center gap-1">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <IconButton aria-label={t('moreOptions')} Icon={<FontAwesomeIcon icon={faEllipsisVertical} />} />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            <OverrideCurrentViewButton data={data} />
            <DropdownMenu.Button StartIcon={<FontAwesomeIcon icon={faTrash} />} onClick={handleDelete}>
              {t('delete')}
            </DropdownMenu.Button>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
        <IconButton
          Icon={<FontAwesomeIcon icon={isDefault ? faXmark : faCheck} />}
          onClick={isDefault ? handleRemoveDefault : handleSetDefault}
        />
      </div>
    </div>
  );
};
